import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useDispatch, useSelector } from "react-redux";
import { Autocomplete } from "@mui/material";
import {
  Download as DownloadIcon,
  Info as InfoIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Stack,
  Tooltip,
  TextField,
  InputAdornment,
  Button,
  useTheme,
  IconButton,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from "@mui/material";
import {
  Search as SearchIcon,
  EventBusy as EventBusyIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import holidaysSvg from "assets/folders/holidays.svg";

import {
  fetchYearlyLeaveReport,
  adjustLeaveBalance,
} from "store/features/company/leaveManagement/leaveDetails/leaveDetailsActions";
import { getLeaveTypes } from "services/company/leaveManagement/leaveType/leaveTypeService";
import {
  selectYearlyLeaveReport,
  selectYearlyLeaveReportLoading,
  selectYearlyLeavePagination,
} from "store/features/company/leaveManagement/leaveDetails/leaveDetailsSelectors";

import usePermissions from "hooks/usePermissions";

const LeaveLedger = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { hasPermission } = usePermissions();

  const report = useSelector(selectYearlyLeaveReport);
  const loading = useSelector(selectYearlyLeaveReportLoading);
  const pagination = useSelector(selectYearlyLeavePagination);

  const [selectedYear, setSelectedYear] = useState(dayjs());
  const [searchTerm, setSearchTerm] = useState("");
  const [legendOpen, setLegendOpen] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustData, setAdjustData] = useState({
    employee_id: "",
    leave_type_id: "",
    action: "credit",
    days: 1,
    reason: "",
  });
  const [adjusting, setAdjusting] = useState(false);
  const [leaveTypes, setLeaveTypes] = useState([]);

  useEffect(() => {
    if (!adjustOpen) return;
    (async () => {
      try {
        const res = await getLeaveTypes();
        if (res?.meta?.status === true) {
          setLeaveTypes(res.data || []);
        }
      } catch (e) {
        console.error("Failed to fetch leave types:", e);
      }
    })();
  }, [adjustOpen]);

  const themeMode = theme.palette.mode;

  // Fetch yearly report on mount + on year change
  useEffect(() => {
    dispatch(fetchYearlyLeaveReport({ year: selectedYear.year() }));
  }, [dispatch, selectedYear]);

  // Filter rows by search (name / code)
  const filteredRows = useMemo(() => {
    if (!report?.rows) return [];
    if (!searchTerm) return report.rows;
    const term = searchTerm.toLowerCase();
    return report.rows.filter(
      (r) =>
        (r.name || "").toLowerCase().includes(term) ||
        (r.employee_code || "").toLowerCase().includes(term),
    );
  }, [report, searchTerm]);

  const months = report?.columns?.months || [];
  const statusColumns = report?.columns?.status_columns || [];
  const stockColumns = report?.columns?.stock_columns || [];
  const overall = report?.overall_summary || null;
  const reportYear = pagination?.year || selectedYear.year();
  const reportTitle = report?.title || `Leave Report ${reportYear}`;

  // A real sample value for the legend "number chip"
  const sampleLeavesValue = useMemo(() => {
    if (!report?.rows?.length || !months.length) return 1;
    for (const r of report.rows) {
      for (let i = 0; i < months.length; i++) {
        const v = r.monthly_details?.[i]?.display_value;
        if (typeof v === "number" && v > 0) return v;
      }
    }
    return 1;
  }, [report, months]);

  // ===== Solid, clean color palette (no transparency) =====
  const colors = {
    border: themeMode === "dark" ? "#334155" : "#d1d5db",
    borderSoft: themeMode === "dark" ? "#1e293b" : "#e5e7eb",
    headerBg: themeMode === "dark" ? "#1e293b" : "#f3f4f6",
    titleBg: themeMode === "dark" ? "#1e293b" : "#fef3c7",
    groupEmpBg: themeMode === "dark" ? "#312e81" : "#e0e7ff",
    groupMonthBg: themeMode === "dark" ? "#1e3a8a" : "#dbeafe",
    groupStatusBg: themeMode === "dark" ? "#78350f" : "#fef3c7",
    groupStockBg: themeMode === "dark" ? "#064e3b" : "#d1fae5",
    rowHover: themeMode === "dark" ? "#1e293b" : "#f9fafb",
    rowEven: themeMode === "dark" ? "#0b1220" : "#fcfcfd",
    summaryBg: themeMode === "dark" ? "#0b1220" : "#f1f5f9",
    summaryBorder: themeMode === "dark" ? "#475569" : "#94a3b8",
    stickyBg: themeMode === "dark" ? "#0f172a" : "#ffffff",
    empGroupBg: themeMode === "dark" ? "#1e1b4b" : "#eef2ff",
  };

  // ===== Cell renderers =====
  const renderMonthCell = (value) => {
    if (value === "-" || value === null || value === undefined) {
      return (
        <Typography
          sx={{
            fontSize: "0.75rem",
            color: themeMode === "dark" ? "#64748b" : "#cbd5e1",
            textAlign: "center",
          }}
        >
          –
        </Typography>
      );
    }
    if (typeof value === "string" && value.toLowerCase().includes("new")) {
      return (
        <Tooltip title="New Joint" arrow>
          <Box
            sx={{
              display: "inline-block",
              px: 0.75,
              py: 0.15,
              borderRadius: "4px",
              fontSize: "0.65rem",
              fontWeight: 700,
              whiteSpace: "nowrap",
              backgroundColor: themeMode === "dark" ? "#1e3a8a" : "#dbeafe",
              color: themeMode === "dark" ? "#93c5fd" : "#1e40af",
            }}
          >
            NJ
          </Box>
        </Tooltip>
      );
    }
    const num = Number(value);
    if (!isNaN(num)) {
      if (num === 0) {
        return (
          <Typography
            sx={{
              fontSize: "0.75rem",
              color: themeMode === "dark" ? "#64748b" : "#94a3b8",
              textAlign: "center",
            }}
          >
            0
          </Typography>
        );
      }
      return (
        <Typography
          sx={{
            fontSize: "0.8rem",
            fontWeight: 700,
            color: themeMode === "dark" ? "#fbbf24" : "#d97706",
            textAlign: "center",
          }}
        >
          {num}
        </Typography>
      );
    }
    return (
      <Typography sx={{ fontSize: "0.75rem", textAlign: "center" }}>
        {String(value)}
      </Typography>
    );
  };

  const renderStatusCell = (value) => {
    const num = Number(value);
    if (!num || num === 0) {
      return (
        <Typography
          sx={{
            fontSize: "0.75rem",
            color: themeMode === "dark" ? "#64748b" : "#94a3b8",
            textAlign: "center",
          }}
        >
          0
        </Typography>
      );
    }
    return (
      <Box
        sx={{
          display: "inline-block",
          px: 0.75,
          py: 0.15,
          borderRadius: "4px",
          fontSize: "0.7rem",
          fontWeight: 700,
          backgroundColor: themeMode === "dark" ? "#78350f" : "#fef3c7",
          color: themeMode === "dark" ? "#fde68a" : "#92400e",
        }}
      >
        {num}
      </Box>
    );
  };

  const renderStockCell = (value) => {
    const num = Number(value);
    if (!num || num === 0) {
      return (
        <Typography
          sx={{
            fontSize: "0.75rem",
            color: themeMode === "dark" ? "#64748b" : "#94a3b8",
            textAlign: "center",
          }}
        >
          0
        </Typography>
      );
    }
    return (
      <Box
        sx={{
          display: "inline-block",
          px: 0.75,
          py: 0.15,
          borderRadius: "4px",
          fontSize: "0.7rem",
          fontWeight: 700,
          backgroundColor: themeMode === "dark" ? "#064e3b" : "#d1fae5",
          color: themeMode === "dark" ? "#6ee7b7" : "#065f46",
        }}
      >
        {num}
      </Box>
    );
  };

  const formatJoiningDate = (d) => {
    if (!d) return "N/A";
    const parsed = dayjs(d);
    return parsed.isValid() ? parsed.format("DD MMM YYYY") : "N/A";
  };

  const cellBaseSx = {
    px: 1,
    py: 0.75,
    borderBottom: `1px solid ${colors.borderSoft}`,
    borderRight: `1px solid ${colors.borderSoft}`,
    textAlign: "center",
    fontSize: "0.8rem",
    whiteSpace: "nowrap",
    minWidth: 46,
  };

  // Column widths (for sticky positioning)
  const COL = {
    slNo: 45,
    employee: 180,
    dept: 160,
  };
  const stickyLeft = {
    slNo: 0,
    employee: COL.slNo,
    dept: COL.slNo + COL.employee,
  };

  // ===== Excel Export =====
  const handleExport = () => {
    if (!report?.rows?.length) {
      alert("No yearly leave data to export");
      return;
    }

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([]);

    const yellowHeaderStyle = {
      font: { bold: true, sz: 11, color: { rgb: "000000" } },
      fill: { fgColor: { rgb: "FFFF00" } },
      alignment: { horizontal: "center", vertical: "center" },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } },
      },
    };
    const statusHeaderStyle = {
      ...yellowHeaderStyle,
      fill: { fgColor: { rgb: "FEF3C7" } },
    };
    const stockHeaderStyle = {
      ...yellowHeaderStyle,
      fill: { fgColor: { rgb: "D1FAE5" } },
    };
    const empHeaderStyle = {
      ...yellowHeaderStyle,
      fill: { fgColor: { rgb: "E0E7FF" } },
    };
    const monthHeaderStyle = {
      ...yellowHeaderStyle,
      fill: { fgColor: { rgb: "DBEAFE" } },
    };

    const empCols = [
      "Sl No",
      "Emp ID",
      "Emp Code",
      "Employee Name",
      "Joining Date",
      "Department",
      "Designation",
    ];
    const totalCols =
      5 + months.length + 1 + statusColumns.length + stockColumns.length;

    let row = 0;

    // Title row
    XLSX.utils.sheet_add_aoa(ws, [[reportTitle]], { origin: `A${row + 1}` });
    ws["!merges"] = [{ s: { r: row, c: 0 }, e: { r: row, c: totalCols - 1 } }];
    ws[`A${row + 1}`].s = {
      font: { bold: true, sz: 14, color: { rgb: "000000" } },
      fill: { fgColor: { rgb: "FFFF00" } },
      alignment: { horizontal: "center", vertical: "center" },
    };
    row++;

    // Meta row
    XLSX.utils.sheet_add_aoa(
      ws,
      [
        [
          `Tenant: Ebluesys  |  Report Year: ${reportYear}  |  Total Employees: ${
            report?.rows?.length || 0
          }`,
        ],
      ],
      { origin: `A${row + 1}` },
    );
    ws["!merges"].push({
      s: { r: row, c: 0 },
      e: { r: row, c: totalCols - 1 },
    });
    ws[`A${row + 1}`].s = {
      font: { bold: true, sz: 10, color: { rgb: "475569" } },
      fill: { fgColor: { rgb: "F1F5F9" } },
      alignment: { horizontal: "left", vertical: "center" },
    };
    row += 2;

    // Group header row
    const groupRow = [
      { v: "Employee Information", c: empCols.length },
      { v: `Monthly Leaves Taken (Jan - Dec ${reportYear})`, c: months.length },
      { v: "Total Leaves", c: 1 },
      { v: "Leave Status (Availed / Approved)", c: statusColumns.length },
      { v: "Leave Stock (Available Balance)", c: stockColumns.length },
    ];
    XLSX.utils.sheet_add_aoa(
      ws,
      [groupRow.flatMap((g) => [g.v, ...Array(g.c - 1).fill("")])],
      { origin: `A${row + 1}` },
    );
    let mergeCol = 0;
    groupRow.forEach((g) => {
      if (g.c > 1) {
        ws["!merges"].push({
          s: { r: row, c: mergeCol },
          e: { r: row, c: mergeCol + g.c - 1 },
        });
      }
      mergeCol += g.c;
    });
    for (let i = 0; i < totalCols; i++) {
      const ref = XLSX.utils.encode_cell({ r: row, c: i });
      if (!ws[ref]) continue;
      if (i < empCols.length) ws[ref].s = empHeaderStyle;
      else if (i < empCols.length + months.length) ws[ref].s = monthHeaderStyle;
      else if (i < empCols.length + months.length + 1)
        ws[ref].s = yellowHeaderStyle;
      else if (i < empCols.length + months.length + 1 + statusColumns.length)
        ws[ref].s = statusHeaderStyle;
      else ws[ref].s = stockHeaderStyle;
    }
    row++;

    // Column headers
    const headers = [
      ...empCols,
      ...months.map((m) => m.label),
      "Total Leaves",
      ...statusColumns.map((c) => `Status_${c}`),
      ...stockColumns.map((c) => `Stock_${c}`),
    ];
    XLSX.utils.sheet_add_aoa(ws, [headers], { origin: `A${row + 1}` });
    for (let i = 0; i < headers.length; i++) {
      const ref = XLSX.utils.encode_cell({ r: row, c: i });
      if (!ws[ref]) continue;
      if (i < empCols.length) ws[ref].s = empHeaderStyle;
      else if (i < empCols.length + months.length) ws[ref].s = monthHeaderStyle;
      else if (i < empCols.length + months.length + 1)
        ws[ref].s = yellowHeaderStyle;
      else if (i < empCols.length + months.length + 1 + statusColumns.length)
        ws[ref].s = statusHeaderStyle;
      else ws[ref].s = stockHeaderStyle;
    }
    row++;

    // Data rows
    filteredRows.forEach((r) => {
      const data = [
        r.sl_no,
        r.employee_id,
        r.employee_code,
        r.name,
        formatJoiningDate(r.joining_date),
        r.department,
        r.designation,
        ...months.map(
          (m, idx) =>
            r.monthly_details?.[idx]?.display_value ?? r[m.label] ?? 0,
        ),
        r.total_leaves ?? 0,
        ...statusColumns.map((c) => r.leave_status?.[c] ?? 0),
        ...stockColumns.map((c) => r.leave_stock?.[c] ?? 0),
      ];
      XLSX.utils.sheet_add_aoa(ws, [data], { origin: `A${row + 1}` });
      row++;
    });

    // Overall summary
    if (overall) {
      const sumRow = [
        overall.label || "Overall Summary",
        "",
        "",
        "",
        "",
        "",
        "",
        ...months.map((m) => overall.monthly_totals?.[m.key] ?? 0),
        overall.total_leaves ?? 0,
        ...statusColumns.map((c) => overall.status_totals?.[c] ?? 0),
        ...stockColumns.map((c) => overall.stock_totals?.[c] ?? 0),
      ];
      XLSX.utils.sheet_add_aoa(ws, [sumRow], { origin: `A${row + 1}` });
      for (let i = 0; i < sumRow.length; i++) {
        const ref = XLSX.utils.encode_cell({ r: row, c: i });
        if (ws[ref]) {
          ws[ref].s = {
            font: { bold: true, sz: 11 },
            fill: { fgColor: { rgb: "E5E7EB" } },
            alignment: { horizontal: "center", vertical: "center" },
            border: yellowHeaderStyle.border,
          };
        }
      }
      ws["!merges"].push({
        s: { r: row, c: 0 },
        e: { r: row, c: empCols.length - 1 },
      });
    }

    ws["!cols"] = [
      { wch: 7 },
      { wch: 8 },
      { wch: 12 },
      { wch: 24 },
      { wch: 14 },
      { wch: 18 },
      { wch: 22 },
      ...months.map(() => ({ wch: 12 })),
      { wch: 12 },
      ...statusColumns.map(() => ({ wch: 9 })),
      ...stockColumns.map(() => ({ wch: 9 })),
    ];

    XLSX.utils.book_append_sheet(wb, ws, "Yearly Leave Report");
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    saveAs(
      blob,
      `Yearly_Leave_Report_${reportYear}_${
        new Date().toISOString().split("T")[0]
      }.xlsx`,
    );
  };

  const handleAdjustSubmit = async () => {
    if (
      !adjustData.employee_id ||
      !adjustData.leave_type_id ||
      !adjustData.days
    ) {
      return;
    }
    setAdjusting(true);
    const result = await dispatch(
      adjustLeaveBalance({
        employee_id: Number(adjustData.employee_id),
        leave_type_id: Number(adjustData.leave_type_id),
        action: adjustData.action,
        days: Number(adjustData.days),
        reason: adjustData.reason || "",
      }),
    );
    setAdjusting(false);
    if (result.success) {
      setAdjustOpen(false);
      setAdjustData({
        employee_id: "",
        leave_type_id: "",
        action: "credit",
        days: 1,
        reason: "",
      });
      dispatch(fetchYearlyLeaveReport({ year: selectedYear.year() }));
    }
  };

  // ===== Permission gate =====
  if (!hasPermission("leave_details.view")) {
    return null;
  }

  const totalCols =
    6 + months.length + 1 + statusColumns.length + stockColumns.length;

  // ===== Render =====
  return (
    <Box sx={{ minHeight: "100vh", p: 1.5 }}>
      {/* Header Section */}
      <Box sx={{ mb: 3 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          sx={{ width: "100%" }}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ flex: 1 }}
          >
            <Box
              component="img"
              src={holidaysSvg}
              alt="Leave Ledger"
              sx={{
                width: { xs: 64, lg: 80 },
                height: { xs: 64, lg: 80 },
                objectFit: "contain",
              }}
            />
            <Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{ fontWeight: "bold", color: "text.primary" }}
              >
                Leave Ledger
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mt: 0.5 }}
              >
                Yearly leave report — status &amp; stock of leaves per employee
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={2} alignItems="center">
            <DatePicker
              label="Select Year"
              views={["year"]}
              value={selectedYear}
              onChange={(v) => v && setSelectedYear(v)}
              slotProps={{
                textField: {
                  size: "small",
                  sx: {
                    width: 160,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      bgcolor: "background.paper",
                    },
                  },
                },
              }}
            />
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              disabled={loading || !report?.rows?.length}
              sx={{
                background: "linear-gradient(135deg, #25a55f 0%, #1e8b4e 100%)",
                color: "#ffffff",
                borderRadius: "8px",
                px: 2.5,
                py: 0.5,
                fontSize: "0.8125rem",
                fontWeight: 500,
                textTransform: "none",
                minWidth: "auto",
                whiteSpace: "nowrap",
                boxShadow: "none",
                transition: "all 0.2s ease",
                height: "36px",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #1e8b4e 0%, #16723e 100%)",
                  boxShadow: "0 2px 8px rgba(37, 165, 95, 0.3)",
                  transform: "translateY(-1px)",
                },
                "&:active": { transform: "translateY(0)" },
              }}
            >
              Export Excel
            </Button>
          </Stack>
        </Stack>
      </Box>

      {/* Search + Legend button */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <TextField
          placeholder="Search by employee name or code..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          sx={{
            width: { xs: "100%", sm: 360 },
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              bgcolor: "background.paper",
              "& fieldset": {
                borderColor: themeMode === "dark" ? "#334155" : "#e5e7eb",
              },
              "&:hover fieldset": { borderColor: "#f59e0b" },
              "&.Mui-focused fieldset": { borderColor: "#f59e0b" },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#9ca3af" }} />
              </InputAdornment>
            ),
          }}
        />

        <Button
          variant="outlined"
          size="small"
          startIcon={<InfoIcon sx={{ fontSize: 16 }} />}
          onClick={() => setLegendOpen(true)}
          sx={{
            borderRadius: "8px",
            px: 2,
            height: 36,
            fontSize: "0.8125rem",
            fontWeight: 500,
            textTransform: "none",
            borderColor: "#f59e0b",
            color: "#f59e0b",
            whiteSpace: "nowrap",
            "&:hover": {
              borderColor: "#d97706",
              backgroundColor: "rgba(245,158,11,0.08)",
            },
          }}
        >
          Column Legend
        </Button>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon sx={{ fontSize: 16 }} />}
          onClick={() => setAdjustOpen(true)}
          sx={{
            borderRadius: "8px",
            px: 2,
            height: 36,
            fontSize: "0.8125rem",
            fontWeight: 500,
            textTransform: "none",
            background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
            color: "#fff",
            whiteSpace: "nowrap",
            "&:hover": {
              background: "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
            },
          }}
        >
          Adjust Leave
        </Button>
      </Stack>
      {/* Table / states */}
      {loading ? (
        <Box
          sx={{
            height: 400,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Stack alignItems="center" spacing={2}>
            <CircularProgress sx={{ color: "primary.main" }} />
            <Typography sx={{ color: "text.secondary" }}>
              Loading yearly leave report...
            </Typography>
          </Stack>
        </Box>
      ) : !report || filteredRows.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            borderRadius: "16px",
            textAlign: "center",
            border: `1px solid ${colors.border}`,
          }}
        >
          <EventBusyIcon sx={{ fontSize: 50, color: "#d1d5db", mb: 1 }} />
          <Typography sx={{ color: "text.secondary" }}>
            {searchTerm
              ? "No employees match your search"
              : `No yearly leave data available for ${reportYear}`}
          </Typography>
        </Paper>
      ) : (
        <Paper
          elevation={0}
          sx={{
            borderRadius: "16px",
            overflow: "hidden",
            border: `1px solid ${colors.border}`,
            boxShadow:
              themeMode === "dark"
                ? "0 10px 25px -5px rgba(0,0,0,0.3)"
                : "0 10px 25px -5px rgba(0,0,0,0.05)",
          }}
        >
          <Box
            sx={{
              overflow: "auto",
              maxHeight: "72vh",
              "&::-webkit-scrollbar": { width: 10, height: 10 },
              "&::-webkit-scrollbar-track": {
                backgroundColor: themeMode === "dark" ? "#1e293b" : "#f1f5f9",
                borderRadius: "5px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#f59e0b",
                borderRadius: "5px",
                "&:hover": { backgroundColor: "#d97706" },
              },
              scrollbarWidth: "thin",
              scrollbarColor: "#f59e0b #f1f5f9",
            }}
          >
            <Box
              component="table"
              sx={{
                borderCollapse: "separate",
                borderSpacing: 0,
                minWidth: "100%",
                tableLayout: "auto",
                "& thead th": { boxSizing: "border-box" },
                "& tfoot td": { boxSizing: "border-box" },
              }}
            >
              {/* ===== HEADER ROW 1: Title ===== */}
              <Box
                component="thead"
                sx={{ position: "sticky", top: 0, zIndex: 40 }}
              >
                <Box component="tr">
                  <Box
                    component="th"
                    colSpan={totalCols}
                    sx={{
                      backgroundColor: colors.titleBg,
                      borderBottom: `1px solid ${colors.border}`,
                      px: 2,
                      py: 0,
                      height: 40,
                      lineHeight: "40px",
                      textAlign: "left",
                      fontWeight: 700,
                      fontSize: "0.95rem",
                      color: themeMode === "dark" ? "#fbbf24" : "#b45309",
                    }}
                  >
                    📊 {reportTitle}
                  </Box>
                </Box>
              </Box>

              {/* ===== HEADER ROW 2: Group headers ===== */}
              <Box
                component="thead"
                sx={{ position: "sticky", top: 40, zIndex: 35 }}
              >
                <Box component="tr">
                  <Box
                    component="th"
                    colSpan={3}
                    sx={{
                      backgroundColor: colors.groupEmpBg,
                      borderBottom: `1px solid ${colors.border}`,
                      borderRight: `1px solid ${colors.border}`,
                      px: 1,
                      py: 0,
                      height: 32,
                      maxHeight: 32,
                      lineHeight: "32px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      textAlign: "center",
                      fontSize: "0.72rem",
                      fontWeight: 800,
                      letterSpacing: "0.3px",
                      boxSizing: "border-box",
                      color: themeMode === "dark" ? "#c7d2fe" : "#3730a3",
                    }}
                  >
                    Employee Information
                  </Box>

                  <Box
                    component="th"
                    colSpan={months.length}
                    sx={{
                      backgroundColor: colors.groupMonthBg,
                      borderBottom: `1px solid ${colors.border}`,
                      borderRight: `1px solid ${colors.border}`,
                      px: 1,
                      py: 0,
                      height: 32,
                      maxHeight: 32,
                      lineHeight: "32px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      textAlign: "center",
                      fontSize: "0.72rem",
                      fontWeight: 800,
                      letterSpacing: "0.3px",
                      boxSizing: "border-box",
                      color: themeMode === "dark" ? "#bfdbfe" : "#1e40af",
                    }}
                  >
                    Monthly Leaves Taken (Jan - Dec {reportYear})
                  </Box>

                  <Box
                    component="th"
                    colSpan={1}
                    sx={{
                      backgroundColor: colors.headerBg,
                      borderBottom: `1px solid ${colors.border}`,
                      borderRight: `1px solid ${colors.border}`,
                      px: 1,
                      py: 0,
                      height: 32,
                      maxHeight: 32,
                      lineHeight: "32px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      textAlign: "center",
                      fontSize: "0.72rem",
                      fontWeight: 800,
                      letterSpacing: "0.3px",
                      boxSizing: "border-box",
                      color: themeMode === "dark" ? "#fbbf24" : "#b45309",
                    }}
                  >
                    Leaves
                  </Box>

                  <Box
                    component="th"
                    colSpan={statusColumns.length}
                    sx={{
                      backgroundColor: colors.groupStatusBg,
                      borderBottom: `1px solid ${colors.border}`,
                      borderRight: `1px solid ${colors.border}`,
                      px: 1,
                      py: 0,
                      height: 32,
                      maxHeight: 32,
                      lineHeight: "32px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      textAlign: "center",
                      fontSize: "0.72rem",
                      fontWeight: 800,
                      letterSpacing: "0.3px",
                      boxSizing: "border-box",
                      color: themeMode === "dark" ? "#fde68a" : "#92400e",
                    }}
                  >
                    Leave Status (Availed / Approved)
                  </Box>

                  <Box
                    component="th"
                    colSpan={stockColumns.length}
                    sx={{
                      backgroundColor: colors.groupStockBg,
                      borderBottom: `1px solid ${colors.border}`,
                      px: 1,
                      py: 0,
                      height: 32,
                      maxHeight: 32,
                      lineHeight: "32px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      textAlign: "center",
                      fontSize: "0.72rem",
                      fontWeight: 800,
                      letterSpacing: "0.3px",
                      boxSizing: "border-box",
                      color: themeMode === "dark" ? "#6ee7b7" : "#065f46",
                    }}
                  >
                    Leave Stock (Available Balance)
                  </Box>
                </Box>
              </Box>

              {/* Row 3 as its own sticky thead — this is the fix */}
              <Box
                component="thead"
                sx={{
                  position: "sticky",
                  top: 72,
                  zIndex: 50,
                }}
              >
                <Box component="tr">
                  {/* Sl No — sticky left only */}
                  <Box
                    component="th"
                    sx={{
                      position: "sticky",
                      left: stickyLeft.slNo,
                      zIndex: 2,
                      backgroundColor:
                        themeMode === "dark" ? "#1e293b" : "#f3f4f6",
                      borderBottom: `1px solid ${colors.border}`,
                      borderRight: `1px solid ${colors.border}`,
                      px: 1,
                      py: 0,
                      height: 36,
                      lineHeight: "36px",
                      textAlign: "center",
                      fontSize: "0.72rem",
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                      width: COL.slNo,
                      minWidth: COL.slNo,
                      maxWidth: COL.slNo,
                      color: themeMode === "dark" ? "#cbd5e1" : "#334155",
                    }}
                  >
                    Sl No
                  </Box>
                  {/* Employee — sticky left only */}
                  <Box
                    component="th"
                    sx={{
                      position: "sticky",
                      left: stickyLeft.employee,
                      zIndex: 2,
                      backgroundColor:
                        themeMode === "dark" ? "#1e293b" : "#f3f4f6",
                      borderBottom: `1px solid ${colors.border}`,
                      borderRight: `1px solid ${colors.border}`,
                      px: 1,
                      py: 0,
                      height: 36,
                      lineHeight: "36px",
                      textAlign: "left",
                      fontSize: "0.72rem",
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                      width: COL.employee,
                      minWidth: COL.employee,
                      maxWidth: COL.employee,
                      color: themeMode === "dark" ? "#c7d2fe" : "#3730a3",
                    }}
                  >
                    Employee
                  </Box>
                  {/* Department — sticky left only */}
                  <Box
                    component="th"
                    sx={{
                      position: "sticky",
                      left: stickyLeft.dept,
                      zIndex: 2,
                      backgroundColor:
                        themeMode === "dark" ? "#1e293b" : "#f3f4f6",
                      borderBottom: `1px solid ${colors.border}`,
                      borderRight: `1px solid ${colors.border}`,
                      px: 1,
                      py: 0,
                      height: 36,
                      lineHeight: "36px",
                      textAlign: "left",
                      fontSize: "0.72rem",
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                      width: COL.dept,
                      minWidth: COL.dept,
                      maxWidth: COL.dept,
                      color: themeMode === "dark" ? "#cbd5e1" : "#334155",
                    }}
                  >
                    Department / Designation
                  </Box>

                  {/* Months — no sticky, inherited from thead */}
                  {months.map((m) => (
                    <Box
                      key={`h-${m.key}`}
                      component="th"
                      sx={{
                        backgroundColor: colors.headerBg,
                        borderBottom: `1px solid ${colors.border}`,
                        borderRight: `1px solid ${colors.border}`,
                        px: 1,
                        py: 0,
                        height: 36,
                        lineHeight: "36px",
                        textAlign: "center",
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                        minWidth: 46,
                        color: themeMode === "dark" ? "#cbd5e1" : "#334155",
                      }}
                    >
                      {m.short_name}
                    </Box>
                  ))}

                  {/* Total */}
                  <Box
                    component="th"
                    sx={{
                      backgroundColor: colors.headerBg,
                      borderBottom: `1px solid ${colors.border}`,
                      borderRight: `1px solid ${colors.border}`,
                      px: 1,
                      py: 0,
                      height: 36,
                      lineHeight: "36px",
                      textAlign: "center",
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                      minWidth: 46,
                      color: themeMode === "dark" ? "#fbbf24" : "#b45309",
                    }}
                  >
                    Total
                  </Box>

                  {/* Status */}
                  {statusColumns.map((c) => (
                    <Box
                      key={`sh-${c}`}
                      component="th"
                      sx={{
                        backgroundColor: colors.groupStatusBg,
                        borderBottom: `1px solid ${colors.border}`,
                        borderRight: `1px solid ${colors.border}`,
                        px: 1,
                        py: 0,
                        height: 36,
                        lineHeight: "36px",
                        textAlign: "center",
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                        minWidth: 46,
                        color: themeMode === "dark" ? "#fde68a" : "#92400e",
                      }}
                    >
                      {c}
                    </Box>
                  ))}

                  {/* Stock */}
                  {stockColumns.map((c) => (
                    <Box
                      key={`sth-${c}`}
                      component="th"
                      sx={{
                        backgroundColor: colors.groupStockBg,
                        borderBottom: `1px solid ${colors.border}`,
                        borderRight: `1px solid ${colors.border}`,
                        px: 1,
                        py: 0,
                        height: 36,
                        lineHeight: "36px",
                        textAlign: "center",
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                        minWidth: 46,
                        color: themeMode === "dark" ? "#6ee7b7" : "#065f46",
                      }}
                    >
                      {c}
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* ===== BODY ===== */}
              <Box component="tbody">
                {filteredRows.map((row, rowIdx) => {
                  const isEven = rowIdx % 2 === 1;
                  const bodyBg = isEven ? colors.rowEven : colors.stickyBg;
                  return (
                    <Box
                      component="tr"
                      key={`row-${row.sl_no}-${row.employee_id}`}
                      sx={{
                        "&:hover td:not([data-sticky])": {
                          backgroundColor: colors.rowHover,
                        },
                      }}
                    >
                      {/* Sl No */}
                      <Box
                        component="td"
                        data-sticky="true"
                        sx={{
                          position: "sticky",
                          left: stickyLeft.slNo,
                          zIndex: 20,
                          backgroundColor: bodyBg,
                          borderBottom: `1px solid ${colors.borderSoft}`,
                          borderRight: `1px solid ${colors.borderSoft}`,
                          px: 1,
                          py: 0.75,
                          textAlign: "center",
                          fontSize: "0.78rem",
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                          width: COL.slNo,
                          minWidth: COL.slNo,
                          maxWidth: COL.slNo,
                        }}
                      >
                        {row.sl_no}
                      </Box>
                      {/* Employee */}
                      <Box
                        component="td"
                        data-sticky="true"
                        sx={{
                          position: "sticky",
                          left: stickyLeft.employee,
                          zIndex: 20,
                          backgroundColor: bodyBg,
                          borderBottom: `1px solid ${colors.borderSoft}`,
                          borderRight: `1px solid ${colors.borderSoft}`,
                          px: 1,
                          py: 0.6,
                          textAlign: "left",
                          width: COL.employee,
                          minWidth: COL.employee,
                          maxWidth: COL.employee,
                        }}
                      >
                        <Tooltip
                          title={`${row.name}${
                            row.employee_code ? ` • ${row.employee_code}` : ""
                          }`}
                          arrow
                        >
                          <Stack spacing={0.15} sx={{ overflow: "hidden" }}>
                            <Typography
                              sx={{
                                fontSize: "0.8rem",
                                fontWeight: 600,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                lineHeight: 1.2,
                              }}
                            >
                              {row.name}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.68rem",
                                fontFamily: "monospace",
                                color:
                                  themeMode === "dark" ? "#94a3b8" : "#64748b",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                lineHeight: 1.2,
                              }}
                            >
                              {row.employee_code || "—"}
                            </Typography>
                          </Stack>
                        </Tooltip>
                      </Box>

                      {/* Department + Designation */}
                      <Box
                        component="td"
                        data-sticky="true"
                        sx={{
                          position: "sticky",
                          left: stickyLeft.dept,
                          zIndex: 20,
                          backgroundColor: bodyBg,
                          borderBottom: `1px solid ${colors.borderSoft}`,
                          borderRight: `1px solid ${colors.borderSoft}`,
                          px: 1,
                          py: 0.6,
                          textAlign: "left",
                          width: COL.dept,
                          minWidth: COL.dept,
                          maxWidth: COL.dept,
                        }}
                      >
                        <Tooltip
                          title={`${row.department || "-"} • ${
                            row.designation || "-"
                          }`}
                          arrow
                        >
                          <Stack spacing={0.15} sx={{ overflow: "hidden" }}>
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                lineHeight: 1.2,
                              }}
                            >
                              {row.department || "—"}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.68rem",
                                color:
                                  themeMode === "dark" ? "#94a3b8" : "#64748b",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                lineHeight: 1.2,
                              }}
                            >
                              {row.designation || "—"}
                            </Typography>
                          </Stack>
                        </Tooltip>
                      </Box>

                      {/* Months */}
                      {months.map((m, idx) => {
                        const detail = row.monthly_details?.[idx];
                        const value =
                          detail?.display_value ?? row[m.label] ?? 0;
                        return (
                          <Box
                            component="td"
                            key={`m-${row.sl_no}-${m.key}`}
                            sx={{
                              borderBottom: `1px solid ${colors.borderSoft}`,
                              borderRight: `1px solid ${colors.borderSoft}`,
                              px: 1,
                              py: 0.75,
                              textAlign: "center",
                              fontSize: "0.78rem",
                              whiteSpace: "nowrap",
                              minWidth: 46,
                              backgroundColor: bodyBg,
                            }}
                          >
                            {renderMonthCell(value)}
                          </Box>
                        );
                      })}

                      {/* Total */}
                      <Box
                        component="td"
                        sx={{
                          borderBottom: `1px solid ${colors.borderSoft}`,
                          borderRight: `1px solid ${colors.borderSoft}`,
                          px: 1,
                          py: 0.75,
                          textAlign: "center",
                          fontWeight: 700,
                          fontSize: "0.8rem",
                          whiteSpace: "nowrap",
                          minWidth: 46,
                          backgroundColor: bodyBg,
                          color: themeMode === "dark" ? "#fbbf24" : "#b45309",
                        }}
                      >
                        {row.total_leaves ?? 0}
                      </Box>

                      {/* Status */}
                      {statusColumns.map((c) => (
                        <Box
                          component="td"
                          key={`s-${row.sl_no}-${c}`}
                          sx={{
                            borderBottom: `1px solid ${colors.borderSoft}`,
                            borderRight: `1px solid ${colors.borderSoft}`,
                            px: 1,
                            py: 0.75,
                            textAlign: "center",
                            fontSize: "0.78rem",
                            whiteSpace: "nowrap",
                            minWidth: 46,
                            backgroundColor: bodyBg,
                          }}
                        >
                          {renderStatusCell(
                            row.leave_status?.[c] ?? row[`Status_${c}`] ?? 0,
                          )}
                        </Box>
                      ))}

                      {/* Stock */}
                      {stockColumns.map((c) => (
                        <Box
                          component="td"
                          key={`st-${row.sl_no}-${c}`}
                          sx={{
                            borderBottom: `1px solid ${colors.borderSoft}`,
                            borderRight: `1px solid ${colors.borderSoft}`,
                            px: 1,
                            py: 0.75,
                            textAlign: "center",
                            fontSize: "0.78rem",
                            whiteSpace: "nowrap",
                            minWidth: 46,
                            backgroundColor: bodyBg,
                          }}
                        >
                          {renderStockCell(
                            row.leave_stock?.[c] ?? row[`Stock_${c}`] ?? 0,
                          )}
                        </Box>
                      ))}
                    </Box>
                  );
                })}
              </Box>
              {/* ===== FOOTER ===== */}
              {overall && (
                <Box component="tfoot">
                  <Box component="tr">
                    {/* Sl No — empty, sticky left */}
                    <Box
                      component="td"
                      sx={{
                        position: "sticky",
                        bottom: 0,
                        left: 0,
                        zIndex: 45,
                        backgroundColor: colors.summaryBg,
                        borderTop: `2px solid ${colors.summaryBorder}`,
                        borderRight: `1px solid ${colors.border}`,
                        px: 1,
                        py: 1,
                        width: COL.slNo,
                        minWidth: COL.slNo,
                        maxWidth: COL.slNo,
                        height: 40,
                      }}
                    />

                    {/* Employee — empty, sticky left */}
                    <Box
                      component="td"
                      sx={{
                        position: "sticky",
                        bottom: 0,
                        left: stickyLeft.employee,
                        zIndex: 45,
                        backgroundColor: colors.summaryBg,
                        borderTop: `2px solid ${colors.summaryBorder}`,
                        borderRight: `1px solid ${colors.border}`,
                        px: 1,
                        py: 1,
                        width: COL.employee,
                        minWidth: COL.employee,
                        maxWidth: COL.employee,
                        height: 40,
                      }}
                    />

                    {/* Department — holds the summary label, sticky left */}
                    <Box
                      component="td"
                      sx={{
                        position: "sticky",
                        bottom: 0,
                        left: stickyLeft.dept,
                        zIndex: 45,
                        backgroundColor: colors.summaryBg,
                        borderTop: `2px solid ${colors.summaryBorder}`,
                        borderRight: `1px solid ${colors.border}`,
                        px: 1.5,
                        py: 1,
                        fontWeight: 800,
                        fontSize: "0.82rem",
                        textAlign: "left",
                        whiteSpace: "normal",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        width: COL.dept,
                        minWidth: COL.dept,
                        maxWidth: COL.dept,
                        height: 40,
                        color: themeMode === "dark" ? "#fbbf24" : "#b45309",
                      }}
                    >
                      {overall.label || "Total"}
                    </Box>

                    {/* Months */}
                    {months.map((m) => (
                      <Box
                        component="td"
                        key={`sum-${m.key}`}
                        sx={{
                          position: "sticky",
                          bottom: 0,
                          zIndex: 40,
                          backgroundColor: colors.summaryBg,
                          borderTop: `2px solid ${colors.summaryBorder}`,
                          borderRight: `1px solid ${colors.border}`,
                          px: 1,
                          py: 1,
                          textAlign: "center",
                          fontWeight: 800,
                          fontSize: "0.82rem",
                          whiteSpace: "nowrap",
                          minWidth: 46,
                          height: 40,
                          color: themeMode === "dark" ? "#fbbf24" : "#d97706",
                        }}
                      >
                        {overall.monthly_totals?.[m.key] ?? 0}
                      </Box>
                    ))}

                    {/* Total */}
                    <Box
                      component="td"
                      sx={{
                        position: "sticky",
                        bottom: 0,
                        zIndex: 40,
                        backgroundColor: colors.summaryBg,
                        borderTop: `2px solid ${colors.summaryBorder}`,
                        borderRight: `1px solid ${colors.border}`,
                        px: 1,
                        py: 1,
                        textAlign: "center",
                        fontWeight: 900,
                        fontSize: "0.88rem",
                        whiteSpace: "nowrap",
                        minWidth: 46,
                        height: 40,
                        color: themeMode === "dark" ? "#fbbf24" : "#b45309",
                      }}
                    >
                      {overall.total_leaves ?? 0}
                    </Box>

                    {/* Status */}
                    {statusColumns.map((c) => (
                      <Box
                        component="td"
                        key={`sums-${c}`}
                        sx={{
                          position: "sticky",
                          bottom: 0,
                          zIndex: 40,
                          backgroundColor: colors.groupStatusBg,
                          borderTop: `2px solid ${colors.summaryBorder}`,
                          borderRight: `1px solid ${colors.border}`,
                          px: 1,
                          py: 1,
                          textAlign: "center",
                          fontWeight: 800,
                          fontSize: "0.8rem",
                          whiteSpace: "nowrap",
                          minWidth: 46,
                          height: 40,
                          color: themeMode === "dark" ? "#fde68a" : "#92400e",
                        }}
                      >
                        {overall.status_totals?.[c] ?? 0}
                      </Box>
                    ))}

                    {/* Stock */}
                    {stockColumns.map((c) => (
                      <Box
                        component="td"
                        key={`sumst-${c}`}
                        sx={{
                          position: "sticky",
                          bottom: 0,
                          zIndex: 40,
                          backgroundColor: colors.groupStockBg,
                          borderTop: `2px solid ${colors.summaryBorder}`,
                          px: 1,
                          py: 1,
                          textAlign: "center",
                          fontWeight: 800,
                          fontSize: "0.8rem",
                          whiteSpace: "nowrap",
                          minWidth: 46,
                          height: 40,
                          color: themeMode === "dark" ? "#6ee7b7" : "#065f46",
                        }}
                      >
                        {overall.stock_totals?.[c] ?? 0}
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Paper>
      )}

      {/* ===== Column Legend Dialog ===== */}
      <Dialog
        open={legendOpen}
        onClose={() => setLegendOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            backgroundColor: themeMode === "dark" ? "#0f172a" : "#ffffff",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontWeight: 700,
            fontSize: "1.05rem",
            pb: 1,
          }}
        >
          Column Legend
          <IconButton
            size="small"
            onClick={() => setLegendOpen(false)}
            sx={{ color: "text.secondary" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            {/* Month cell legend */}
            <Box>
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  color: themeMode === "dark" ? "#94a3b8" : "#64748b",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  mb: 1,
                }}
              >
                Monthly Cell Values
              </Typography>
              <Stack spacing={1}>
                {[
                  {
                    chip: (
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          color: themeMode === "dark" ? "#64748b" : "#cbd5e1",
                          minWidth: 45,
                          textAlign: "center",
                        }}
                      >
                        –
                      </Typography>
                    ),
                    label: "Not joined yet (pre-joining month)",
                  },
                  {
                    chip: (
                      <Box
                        sx={{
                          display: "inline-block",
                          px: 0.75,
                          py: 0.15,
                          borderRadius: "4px",
                          fontSize: "0.65rem",
                          fontWeight: 700,
                          minWidth: 45,
                          textAlign: "center",
                          backgroundColor:
                            themeMode === "dark" ? "#1e3a8a" : "#dbeafe",
                          color: themeMode === "dark" ? "#93c5fd" : "#1e40af",
                        }}
                      >
                        NJ
                      </Box>
                    ),
                    label: "New Joint — employee joined that month",
                  },
                  {
                    chip: (
                      <Typography
                        sx={{
                          fontSize: "0.8rem",
                          fontWeight: 700,
                          color: themeMode === "dark" ? "#fbbf24" : "#d97706",
                          minWidth: 45,
                          textAlign: "center",
                        }}
                      >
                        {sampleLeavesValue}
                      </Typography>
                    ),
                    label: "Leaves taken in that month (e.g. from your data)",
                  },
                ].map((item, i) => (
                  <Stack
                    key={i}
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    sx={{
                      p: 1,
                      borderRadius: "8px",
                      backgroundColor:
                        themeMode === "dark" ? "#1e293b" : "#f1f5f9",
                    }}
                  >
                    <Box sx={{ minWidth: 45 }}>{item.chip}</Box>
                    <Typography
                      sx={{
                        fontSize: "0.8rem",
                        color: themeMode === "dark" ? "#cbd5e1" : "#475569",
                      }}
                    >
                      {item.label}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>

            {/* Status columns */}
            <Box>
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  color: themeMode === "dark" ? "#fbbf24" : "#92400e",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  mb: 1,
                }}
              >
                Leave Status Columns (Availed / Approved)
              </Typography>
              <Stack spacing={0.75}>
                {statusColumns.map((c) => (
                  <Stack
                    key={c}
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    sx={{
                      p: 1,
                      borderRadius: "8px",
                      backgroundColor: colors.groupStatusBg,
                    }}
                  >
                    <Box
                      sx={{
                        minWidth: 45,
                        textAlign: "center",
                        fontWeight: 700,
                        fontSize: "0.8rem",
                        color: themeMode === "dark" ? "#fde68a" : "#92400e",
                      }}
                    >
                      {c}
                    </Box>
                    <Typography
                      sx={{
                        fontSize: "0.8rem",
                        color: themeMode === "dark" ? "#cbd5e1" : "#475569",
                      }}
                    >
                      {c === "CL" &&
                        "Casual Leave — leaves availed from CL balance"}
                      {c === "EL" &&
                        "Earned Leave — leaves availed from EL balance"}
                      {c === "ML" &&
                        "Maternity Leave — leaves availed from ML balance"}
                      {c === "SPL" &&
                        "Special Leave — leaves availed from SPL balance"}
                      {c === "SL" &&
                        "Sick Leave — leaves availed from SL balance"}
                      {!["CL", "EL", "ML", "SPL", "SL"].includes(c) &&
                        `Leaves availed from ${c} balance`}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>

            {/* Stock columns */}
            <Box>
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  color: themeMode === "dark" ? "#6ee7b7" : "#065f46",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  mb: 1,
                }}
              >
                Leave Stock Columns (Available Balance)
              </Typography>
              <Stack spacing={0.75}>
                {stockColumns.map((c) => (
                  <Stack
                    key={c}
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    sx={{
                      p: 1,
                      borderRadius: "8px",
                      backgroundColor: colors.groupStockBg,
                    }}
                  >
                    <Box
                      sx={{
                        minWidth: 45,
                        textAlign: "center",
                        fontWeight: 700,
                        fontSize: "0.8rem",
                        color: themeMode === "dark" ? "#6ee7b7" : "#065f46",
                      }}
                    >
                      {c}
                    </Box>
                    <Typography
                      sx={{
                        fontSize: "0.8rem",
                        color: themeMode === "dark" ? "#cbd5e1" : "#475569",
                      }}
                    >
                      {c === "CL" && "Remaining Casual Leave balance"}
                      {c === "EL" && "Remaining Earned Leave balance"}
                      {c === "ML" && "Remaining Maternity Leave balance"}
                      {c === "SL" && "Remaining Sick Leave balance"}
                      {!["CL", "EL", "ML", "SL"].includes(c) &&
                        `Remaining ${c} balance`}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>

            {/* Notes */}
            <Box>
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  color: themeMode === "dark" ? "#94a3b8" : "#64748b",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  mb: 1,
                }}
              >
                Notes
              </Typography>
              <Stack spacing={0.5} sx={{ pl: 1 }}>
                <Typography
                  sx={{
                    fontSize: "0.78rem",
                    color: themeMode === "dark" ? "#cbd5e1" : "#475569",
                  }}
                >
                  • <b>Total Leaves</b> = sum of all monthly leaves taken in the
                  year.
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.78rem",
                    color: themeMode === "dark" ? "#cbd5e1" : "#475569",
                  }}
                >
                  • <b>Status</b> = leaves already consumed from that leave
                  type.
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.78rem",
                    color: themeMode === "dark" ? "#cbd5e1" : "#475569",
                  }}
                >
                  • <b>Stock</b> = leaves still available to the employee.
                </Typography>
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            onClick={() => setLegendOpen(false)}
            size="small"
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              px: 3,
              background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
              color: "#ffffff",
              fontSize: "0.8rem",
              fontWeight: 500,
              "&:hover": {
                background: "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
              },
            }}
          >
            Got it
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== Adjust Leave Modal ===== */}
      <Dialog
        open={adjustOpen}
        onClose={() => setAdjustOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem" }}>
          Adjust Leave Balance
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Autocomplete
              options={report?.rows || []}
              getOptionLabel={(opt) =>
                `${opt.name || ""}${
                  opt.employee_code ? ` (${opt.employee_code})` : ""
                }`
              }
              value={
                (report?.rows || []).find(
                  (r) =>
                    String(r.employee_id) === String(adjustData.employee_id),
                ) || null
              }
              onChange={(e, newValue) =>
                setAdjustData((p) => ({
                  ...p,
                  employee_id: newValue ? newValue.employee_id : "",
                }))
              }
              isOptionEqualToValue={(opt, val) =>
                String(opt.employee_id) === String(val.employee_id)
              }
              size="small"
              fullWidth
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Employee"
                  placeholder="Type name or code"
                />
              )}
            />

            <TextField
              select
              label="Leave Type"
              value={adjustData.leave_type_id}
              onChange={(e) =>
                setAdjustData((p) => ({ ...p, leave_type_id: e.target.value }))
              }
              size="small"
              fullWidth
            >
              {leaveTypes.map((lt) => (
                <MenuItem key={lt.id} value={lt.id}>
                  {lt.name} ({lt.code})
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Action"
              value={adjustData.action}
              onChange={(e) =>
                setAdjustData((p) => ({ ...p, action: e.target.value }))
              }
              size="small"
              fullWidth
            >
              <MenuItem value="credit">Credit (add leaves)</MenuItem>
              <MenuItem value="debit">Debit (deduct leaves)</MenuItem>
            </TextField>

            <TextField
              label="Days"
              type="number"
              value={adjustData.days}
              onChange={(e) =>
                setAdjustData((p) => ({ ...p, days: e.target.value }))
              }
              size="small"
              fullWidth
              inputProps={{ min: 0.5, step: 0.5 }}
            />

            <TextField
              label="Reason"
              value={adjustData.reason}
              onChange={(e) =>
                setAdjustData((p) => ({ ...p, reason: e.target.value }))
              }
              size="small"
              fullWidth
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            p: 2,
            pt: 0,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Button
            onClick={() => setAdjustOpen(false)}
            disabled={adjusting}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>

          <Button
            onClick={handleAdjustSubmit}
            disabled={
              adjusting ||
              !adjustData.employee_id ||
              !adjustData.leave_type_id ||
              !adjustData.days
            }
            variant="contained"
            sx={{
              textTransform: "none",
              background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
              },
            }}
          >
            {adjusting ? (
              <CircularProgress size={20} sx={{ color: "#fff" }} />
            ) : (
              "Submit"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeaveLedger;
